import { NgClass } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { InfoComponent } from "@components/info/info.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { NgbModal, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ArbetsorderListHoverInMessage, ArbetsorderListHoverOutMessage, ArbetsorderMapHoverInMessage, ArbetsorderMapHoverOutMessage } from "../../events/ArbetsorderHoverMessage";
import { IKeyValueGroup, ITplArbetsorder } from "../../models/dtos";
import { TplService } from "../../services/tpl.service";
import { TplStateService } from "../../state/tpl-state.service";
import { TplArbetsorderInfoModalComponent } from "../tpl-arbetsorder-info-modal/tpl-arbetsorder-info-modal.component";
import { TplDestineraModalComponent } from "./components/tpl-destinera-modal/tpl-destinera-modal.component";
import { TplWarningModalComponent } from "./components/tpl-warning-modal/tpl-warning-modal.component";
import { TplOmdestineraViewModel } from "./tpl-omdestinera.models";

@Component({
    selector: "rask-tpl-omdestinera",
    templateUrl: "./tpl-omdestinera.component.html",
    styleUrls: ["./tpl-omdestinera.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        ButtonComponent,
        SelectorComponent,
        FormsModule,
        CheckboxComponent,
        NgClass,
        InfoComponent,
        NgbTooltip,
        TranslatePipe
    ]
})
export class TplOmdestineraComponent implements OnInit, OnDestroy {
    allSelected = false;
    arbetsorders: TplOmdestineraViewModel[] = [];
    canDestinera = false;
    @Input() canEdit: boolean;
    handelssortiment: IKeyValueGroup[] = [];
    nrOfSelectedAos = 0;
    selectedHandelssortiment: number;
    showDetails = false;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly tplService = inject(TplService);
    private readonly tplStateService = inject(TplStateService);

    aoDetails($event, arbetsorderId: number) {
        $event.preventDefault();

        const arbetsorder = this.arbetsorders.find(ao => ao.id === arbetsorderId);
        TplArbetsorderInfoModalComponent.open({
            arbetsorderId: arbetsorderId,
            isSelected: arbetsorder.isSelected,
            showSelected: true
        }, this.modalService, result => {
            if (arbetsorder.isSelected !== result.isSelected) {
                arbetsorder.isSelected =  result.isSelected;
                this.onCheckedChanged(arbetsorder.isSelected);
            }

            // triggar refresh i kartan
            const aoidlist = [arbetsorderId];
            this.attachHandelssortiment(aoidlist, this.selectedHandelssortiment);
            this.signalHandelssortiment(this.selectedHandelssortiment);
        });
    }

    attachHandelssortiment(arbetsorderIds: number[], handelssortimentId: number): void {
        if (!handelssortimentId) {
            return;
        }
        const hsnamn = this.tplService.resolveHandelssortiment(handelssortimentId)?.value;
        const applicableArbetsordrar = this.arbetsorders.filter(x => arbetsorderIds.some(x1 => x1 === x.id));
        applicableArbetsordrar.forEach(x => {
            const handelssortiment = x.model.handelssortiment.find(hs => hs.handelssortimentId === handelssortimentId);
            if (!handelssortiment) {
                return;
            }

            x.handelssortimentInfo = {
                arbetsorderId: x.id,
                handelssortimentId: handelssortiment.handelssortimentId,
                handelssortimentNamn: hsnamn,
                planeradVolym: handelssortiment.planeradVolym,
                vagVolym: handelssortiment.vagVolym,
                destineringar: handelssortiment.destineringar
                    .map(x1 => ({
                        andelProcent: x1.andelProcent,
                        namn: this.tplService.resolveMottagningsplats(x1.mottagningsplatsId)?.value
                    })),
                hasInaktivaDestineringar: handelssortiment.hasInaktivaDestineringar
            };

            x.hasVaglager = x.handelssortiment.some(x1 => x1.vagVolym > 0);
            x.isDestinerad = x.handelssortiment.some(x1 => x1.destineringar?.length > 0);
            x.allDestinerad = x.handelssortiment.every(x1 => x1.destineringar?.length > 0);
            x.isDelad = x.isDestinerad && !x.allDestinerad;
        });

        this.sortArbetsorders();
    }

    clearWarnings(selected: TplOmdestineraViewModel[], clearSelected: boolean): void {
        selected?.forEach(ao => {
            ao.warningVisible = false;
            ao.hasWarning = false;
            ao.warnings = [];

            if(clearSelected) {
                ao.isSelected = false;
            }
        });
    }

    createViewModel(arbetsordrar: ITplArbetsorder[]) {
        this.arbetsorders = arbetsordrar.map(x => new TplOmdestineraViewModel(x));
        this.sortArbetsorders();
    }

    formatter = (x: any) => x ? x.key : undefined;

    getCurrentHs() {
        const hs = this.tplStateService.current.getSelectedHandelssortiment();
        return ObjectUtils.hasValue(hs) && hs.length === 1 ? hs[0] : null;
    }

    selectHandelssortiment = ($event: any) => {
        // eslint-disable-next-line sonarjs/no-inverted-boolean-check
        if (!!$event && !(typeof $event === "number")) {
            return;
        }

        this.arbetsorders.forEach(ao => {
            ao.handelssortimentInfo = null;
        });
        this.selectedHandelssortiment = $event;
        this.signalHandelssortiment($event);

        if (!ObjectUtils.hasValue($event)) {
            this.showDetails = false;
            this.canDestinera = false;
            this.sortArbetsorders();
            return;
        } else {
            this.showDetails = true;
        }

        this.attachHandelssortiment(this.arbetsorders.map(x => x.id), $event);
        this.canDestinera = this.nrOfSelectedAos > 0 && this.showDetails === true;
    };

    initHandelssortiment = () => {
        // eslint-disable-next-line sonarjs/no-inverted-boolean-check
        if (!(this.arbetsorders.length > 0)) {
            return;
        }

        const tentativeHandelssortiment = this.tplService.getUniqueHandelssortiment(this.arbetsorders);
        const isBransle = this.tplStateService.current.transportsatt.value.bransle;
        this.handelssortiment = tentativeHandelssortiment.filter(x => this.tplService.handelssortimentMatchTransportsatt(x.group, isBransle));

        this.selectHandelssortiment(this.getCurrentHs());
    };

    mouseOut(arbetsorderId: number) {
        this.arbetsorders.find(p => p.id === arbetsorderId).hover = false;
        this.eventService.emit(new ArbetsorderListHoverOutMessage(arbetsorderId));
    }

    mouseOver(arbetsorderId: number) {
        this.arbetsorders.find(p => p.id === arbetsorderId).hover = true;
        this.eventService.emit(new ArbetsorderListHoverInMessage(arbetsorderId));
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        const state = this.tplStateService.current;
        state.currentArbetsordrar
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(current => {
                this.createViewModel(current);
                this.initHandelssortiment();
                this.tplStateService.current.setArbetsordrarDestinering(this.arbetsorders);
            });

        state.selected
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (this.arbetsorders && event.origin !== "omdestinera") {
                    this.arbetsorders.forEach(ao => {
                        ao.isSelected = event.selected.some(id => id === ao.id);
                    });
                    this.updateSelectedView();
                }
            });

        this.eventService.subscribeEvent<ArbetsorderMapHoverInMessage>(ArbetsorderMapHoverInMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderMapHoverInMessage) => {
                this.arbetsorders.find(p => p.id === event.arbetsorderId).hover = true;
                this.triggerDigestCycle();
            });

        this.eventService.subscribeEvent<ArbetsorderMapHoverOutMessage>(ArbetsorderMapHoverOutMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderMapHoverOutMessage) => {
                this.arbetsorders.find(p => p.id === event.arbetsorderId).hover = false;
                this.triggerDigestCycle();
            });
    }

    onAllSelectedChanged($event: boolean) {
        this.allSelected = $event;
        this.arbetsorders.forEach(x => x.isSelected = $event);
        this.updateSelectedView();
        this.signalSelected();
    }

    onCheckedChanged($event: any) {
        setTimeout(() => {
            this.updateSelectedView();
            if ($event != null) {
                this.signalSelected();
            }
        }, 200);
    }

    openDialog() {
        const selected = this.arbetsorders.filter(x => x.isSelected);
        TplDestineraModalComponent.open(this.modalService, selected, result => {
            const isBransle = this.tplStateService.current.transportsatt.value.bransle;
            if(result === "delete") {
                this.clearWarnings(selected, true);
                this.signalSelected();
            } else if (result === false) {
                this.clearWarnings(selected, false);
            } else {
                selected.forEach(ao => ao.warningVisible = ao.hasWarning);
                selected.forEach(ao => {
                    if(!ao.hasWarning && ao.isDestinerad && !isBransle) {
                        ao.isSelected = false;
                    }
                });
                this.signalSelected();
            }
            const aoidlist = selected.map(x => x.id);
            this.refreshHandelssortiment(aoidlist, this.selectedHandelssortiment);
        }, "lg");
    }

    outputformatter = (x: any) => x ? x.value : undefined;

    refreshHandelssortiment(arbetsorderIds: number[], handelssortimentId: number): void {
        this.tplService
            .refresh(arbetsorderIds)
            .subscribe(result => {
                const modified = [];
                result.forEach(r => {
                    const ao = this.arbetsorders.find(x => x.id === r.id);
                    ao.model.handelssortiment = r.handelssortiment;
                    modified.push(ao);
                });

                this.attachHandelssortiment(arbetsorderIds, handelssortimentId);
                this.tplStateService.current.modifyArbetsordrar(modified);
            });
    }

    showWarningInfo(arbetsorderId: number) {
        const ao = this.arbetsorders.find(x => x.id === arbetsorderId);
        TplWarningModalComponent.open(ao, this.modalService, (r) => {
            ao.warningVisible = false;
        });
    }

    signalSelected() {
        const currentSelected = this.arbetsorders.filter(x => x.isSelected).map(x => x.id);
        this.tplStateService.current.setSelected({ selected: currentSelected,
            origin: "omdestinera" });
    }

    updateSelectedView() {
        this.setNrOfSelectedAos();
        this.sortArbetsorders();
    }

    private setNrOfSelectedAos() {
        this.nrOfSelectedAos = this.arbetsorders.filter(x => x.isSelected).length;
        this.canDestinera = this.nrOfSelectedAos > 0 && this.showDetails === true;
        this.allSelected = this.nrOfSelectedAos === this.arbetsorders.length;
    }

    private signalHandelssortiment(hs) {
        this.tplStateService.current.setDestineringHandelssortiment(hs);
    }

    private sortArbetsorders() {
        this.arbetsorders = _.orderBy(this.arbetsorders, [
            "isSelected",
            "handelssortimentInfo",
            "northing"
        ], [
            "desc",
            "asc",
            "desc"
        ]);
    }

    private triggerDigestCycle(): void {
        this.changeDetectorRef.detectChanges();
    }
}
